import { useRef, useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Package, AlertCircle, FileText, CheckCircle, Plus, Download, Filter, ArrowUp, ArrowDown, ArrowUpDown, Loader2, RefreshCw, Search, X } from 'lucide-react';
import { POHeaderFilterSections } from './poheaders/POHeaderFilterSections';
import { POHeaderFilterOptions } from './poheaders/usePOHeaderFilterCascades';
import { PurchaseOrder, purchasingAPI } from '../../../lib/api';
import { Pagination } from '../../ui/pagination';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { useView } from '../../../contexts/ViewContext';
import { toast } from 'sonner';

interface POHeadersTabProps {
  orders: PurchaseOrder[];
  loading: boolean;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onRowClick: (poId: number) => void;
  onPONumberClick: (poNumber: string) => void;
  textInputs: {
    poNumber: string;
    supplier: string;
    customer: string;
  };
  filters: {
    poNumber: string;
    supplier: string;
    customer: string;
    status: string;
    dateFrom: string;
    dateTo: string;
    site: string;
    /** CR-6446: Warehouse filter for Site → Warehouse cascade. */
    warehouse: string;
    division: string;
    companyCountry: string;
    supplierGroup: string;
    supplierCountry: string;
    commodityGroup: string;
    commodityType: string;
    pdfAttached: string;
    strategic: string;
    ackStatus: string;  // CR-6679: Header-level acknowledgement status filter
  };
  siteOptions?: { value: string; label: string }[];
  supplierGroupOptions?: { value: string; label: string }[];
  commodityGroupOptions?: { value: string; label: string }[];
  commodityTypeOptions?: { value: string; label: string }[];
  /** CR-6439: Raw commodity types with commodity_group relationship for cascade filtering */
  rawCommodityTypes?: Array<{ id: number; name: string; commodity_group: number }>;
  /** CR-6439: Division options for cascade filtering */
  divisionOptions?: { value: string; label: string }[];
  /** CR-6439: Site country options for cascade filtering */
  siteCountries?: string[];
  /** CR-6439: Supplier country options for cascade filtering */
  supplierCountries?: string[];
  onFilterChange: (key: string, value: string) => void;
  onClearFilters: () => void;
  showFilters: boolean;
  activeTab: string;
  onTabChange: (tab: string) => void;
  onRefresh?: () => void;
  onSearch?: () => void;
  onToggleFilters?: () => void;
  tabCounts?: Record<string, number>;
  openOrderValue?: number;
  /** True when the active filters are not applied to the open-order-value figure. */
  openOrderValueUnscoped?: boolean;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  /**
   * My/All suppliers scope for the export, mirroring the page toggle:
   * `false` = "my suppliers" (scope the download to the buyer's codes),
   * `true` = "all suppliers", `undefined` = user has no buyer codes (no scope).
   */
  exportAllSuppliers?: boolean;
  /** CR-6446: Whether this is supplier view (SO Headers) vs customer view (PO Headers). */
  isSupplier?: boolean;
}

export default function POHeadersTab({
  orders,
  loading: _loading,
  totalCount,
  currentPage,
  totalPages,
  onPageChange,
  onRowClick,
  onPONumberClick,
  textInputs,
  filters,
  siteOptions = [],
  supplierGroupOptions = [],
  commodityGroupOptions = [],
  commodityTypeOptions = [],
  rawCommodityTypes = [],
  divisionOptions = [],
  siteCountries = [],
  supplierCountries = [],
  onFilterChange,
  onClearFilters,
  showFilters,
  activeTab,
  onTabChange,
  onRefresh,
  onSearch,
  onToggleFilters,
  tabCounts = {},
  openOrderValue = 0,
  openOrderValueUnscoped = false,
  sortField = '-issue_date',
  sortOrder: _sortOrder = 'desc',
  onSort,
  exportAllSuppliers,
  isSupplier = false,
}: POHeadersTabProps) {
  const { formatDate: contextFormatDate, formatCurrency, formatCurrencyFromBase } = useFormatters();
  // CR-4577: Filter site options by view context
  const { viewLevel, selectedCompanyId, selectedGroupId, getViewQueryParams } = useView();
  const [exporting, setExporting] = useState(false);
  const viewScopedSites = useMemo(() => {
    if (viewLevel === 'SITE' && selectedCompanyId) return siteOptions.filter(s => s.value === String(selectedCompanyId));
    if (viewLevel === 'COMPANY' && selectedCompanyId) return siteOptions.filter(s => s.value === String(selectedCompanyId));
    return siteOptions;
  }, [siteOptions, viewLevel, selectedCompanyId, selectedGroupId]);

  const SortHeader = ({ field, label, align = 'left' }: { field: string; label: string; align?: string }) => {
    const active = sortField === field || sortField === `-${field}`;
    const isDesc = sortField.startsWith('-');
    return (
      <th
        className={`text-${align} py-3 px-3 font-medium cursor-pointer hover:bg-muted/50 select-none`}
        onClick={() => onSort?.(field)}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          {active ? (
            isDesc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />
          )}
        </span>
      </th>
    );
  };
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const certInputRef = useRef<HTMLInputElement>(null);
  const supplierSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const uploadTargetRef = useRef<{ poId: number; type: 'po_pdf' | 'material_cert_customer' } | null>(null);
  // CR-6439: Store full supplier data including group, country, and customers for cascade filtering
  const [allSuppliers, setAllSuppliers] = useState<{
    id: number;
    name: string;
    group?: number | null;
    country?: string | null;
    customers?: number[];  // Site IDs this supplier serves
  }[]>([]);
  // CR-6446: Customer options for supplier view (includes group and country for cascade filtering)
  const [allCustomers, setAllCustomers] = useState<{
    id: number;
    name: string;
    group?: number | null;
    country?: string | null;
  }[]>([]);
  // CR-6446: Warehouse options for Site → Warehouse cascade
  const [allWarehouses, setAllWarehouses] = useState<{
    id: number;
    name: string;
    site: number;
  }[]>([]);

  // Load suppliers/customers/warehouses once from PO lines filter options (accessible to all users)
  // CR-6439: Commodity types now come from props (rawCommodityTypes) with commodity_group relationship
  useEffect(() => {
    purchasingAPI.getLineFilterOptions().then(res => {
      // CR-6439: Store full supplier data including group field for cascade filtering
      setAllSuppliers(res.data.suppliers || []);
      // CR-6446: For supplier view, load customer options
      setAllCustomers(res.data.customers || []);
      // CR-6446: Load warehouse options for Site → Warehouse cascade
      setAllWarehouses(res.data.warehouses || []);
    }).catch(() => {});
  }, []);

  // CR-6439: Build raw filter options for cascade filtering
  // Uses allSuppliers from getLineFilterOptions (has group, country, customers fields)
  // Uses allCustomers for site data with group (division) and country
  // Uses rawCommodityTypes from props (from Purchasing.tsx getCommodityTypes API - has commodity_group field)
  // CR-6446: Uses allWarehouses for Site → Warehouse cascade
  const rawFilterOptions: POHeaderFilterOptions | null = useMemo(() => {
    // Only provide raw options when we have the data needed for cascading
    if (allSuppliers.length === 0 && rawCommodityTypes.length === 0) {
      return null;
    }
    return {
      suppliers: allSuppliers,
      commodity_types: rawCommodityTypes,
      commodity_groups: commodityGroupOptions.map((g) => ({ id: parseInt(g.value, 10), name: g.label })),
      supplier_groups: supplierGroupOptions.map((g) => ({ id: parseInt(g.value, 10), name: g.label })),
      // CR-6439: Sites with group (division) and country for cascade filtering
      sites: allCustomers.length > 0
        ? allCustomers.map((c) => ({ id: c.id, name: c.name, group: c.group, country: c.country }))
        : siteOptions.map((s) => ({ id: parseInt(s.value, 10), name: s.label })),
      // CR-6446: Warehouses with site parent for Site → Warehouse cascade
      warehouses: allWarehouses,
      divisions: divisionOptions.map((d) => ({ id: parseInt(d.value, 10), name: d.label })),
      site_countries: siteCountries,
      supplier_countries: supplierCountries,
    };
  }, [allSuppliers, allCustomers, allWarehouses, rawCommodityTypes, commodityGroupOptions, supplierGroupOptions, siteOptions, divisionOptions, siteCountries, supplierCountries]);

  /**
   * Build the query params used by the PO list endpoint, then call the
   * backend /purchasing/orders/export/ action so the resulting xlsx
   * contains the FULL filtered set (not just the page currently rendered).
   * Mirrors the param mapping in pages/Purchasing.tsx fetchData().
   */
  const handleExport = async () => {
    try {
      setExporting(true);
      const queryParams: Record<string, string | number | boolean> = {
        file_format: 'xlsx',
      };

      if (filters.poNumber) {
        queryParams['po_number__icontains'] = filters.poNumber;
      }
      // CR-7232: read textInputs.supplier, the slot the grid fetch and the
      // toolbar box use. This previously read filters.supplier, which only the
      // ?supplier= deep link ever set — so a supplier chosen in the panel
      // filtered the grid but exported the whole unfiltered set.
      //
      // Pass the name through as-is so the icontains lookup matches what's
      // actually stored (e.g. "KNORR-BREMSE SYSTEMS LTD" or "ROS ROCA S.A.").
      // Normalising to spaces silently returned zero rows.
      if (textInputs.supplier) {
        // Same numeric-ID branch as Purchasing.tsx fetchData: the deep link can
        // still carry a raw supplier id if the name lookup failed.
        const isNumericId = /^\d+$/.test(textInputs.supplier);
        if (isNumericId) {
          queryParams['supplier'] = textInputs.supplier;
        } else {
          queryParams['supplier__name__icontains'] = textInputs.supplier;
        }
      }
      if (filters.customer) {
        queryParams['customer__name__icontains'] = filters.customer;
      }
      if (filters.status) {
        queryParams['status'] = filters.status;
      } else if (activeTab !== 'all') {
        if (activeTab === 'open') queryParams['status__in'] = 'sent,acknowledged,in_progress';
        else if (activeTab === 'delivered') queryParams['status'] = 'delivered';
        else if (activeTab === 'closed') queryParams['status'] = 'closed';
      }
      if (filters.dateFrom) queryParams['issue_date__gte'] = filters.dateFrom;
      if (filters.dateTo) queryParams['issue_date__lte'] = filters.dateTo;
      if (filters.site) queryParams['customer'] = filters.site;
      if (filters.supplierGroup) queryParams['supplier__group'] = filters.supplierGroup;
      if (filters.commodityGroup) queryParams['lines__commodity_group'] = filters.commodityGroup;
      if (filters.commodityType) queryParams['lines__commodity_type'] = filters.commodityType;

      // Buyer-code scope — mirror the page's My / All suppliers toggle so the
      // export matches the list. Sent explicitly (true AND false): the backend
      // leaves exports whole-company unless it sees all_suppliers=false, which
      // scopes the download to "my suppliers".
      if (exportAllSuppliers !== undefined) queryParams['all_suppliers'] = exportAllSuppliers;

      // View context (group/company/site) — must match list call so the export
      // is scoped the same way the table is.
      const viewParams = getViewQueryParams();
      Object.entries(viewParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams[key] = value;
        }
      });

      const response = await purchasingAPI.exportOrders(queryParams);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `purchase-orders-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Exported purchase orders to Excel');
    } catch (err: unknown) {
      console.error('PO Headers export failed:', err);
      const message = err instanceof Error ? err.message : 'Export failed';
      toast.error(`Failed to export purchase orders: ${message}`);
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return contextFormatDate(dateString);
  };

  const hasOnHoldLines = (order: PurchaseOrder) => {
    return order.lines?.some(line => line.on_hold) || false;
  };

  // CR-936: the list payload carries has_pdf (an Exists annotation), not the
  // documents array the list never sent - so every row used to read "no PDF".
  // Same definition as the pdf_attached filter: a po_pdf document, nothing else.
  const hasPDFAttached = (order: PurchaseOrder) => {
    if (typeof order.has_pdf === 'boolean') return order.has_pdf;
    return order.documents?.some(doc => doc.document_type === 'po_pdf') || false;
  };

  const hasMaterialCert = (order: PurchaseOrder) => {
    return order.documents?.some(doc =>
      doc.document_type === 'material_cert_customer' ||
      doc.document_type === 'certificate' ||
      doc.file_name?.toLowerCase().includes('cert')
    ) || false;
  };

  const handleUploadClick = (e: React.MouseEvent, poId: number, type: 'po_pdf' | 'material_cert_customer') => {
    e.stopPropagation();
    uploadTargetRef.current = { poId, type };
    if (type === 'po_pdf') {
      pdfInputRef.current?.click();
    } else {
      certInputRef.current?.click();
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTargetRef.current) return;

    const { poId, type } = uploadTargetRef.current;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('po', String(poId));
    formData.append('document_type', type);
    formData.append('source', 'customer');

    try {
      await purchasingAPI.uploadPODocument(formData);
      toast.success(`${type === 'po_pdf' ? 'PDF' : 'Certificate'} uploaded successfully`);
      onRefresh?.();
    } catch (err: unknown) {
      console.error('Upload failed:', err);
      toast.error('Failed to upload document');
    }

    // Reset input
    e.target.value = '';
    uploadTargetRef.current = null;
  };

  /**
   * CR-7232: toolbar supplier keystroke handler.
   *
   * The value lands in textInputs.supplier immediately so the box stays
   * responsive, but the fetch is debounced by 500ms — matching the PO Lines
   * quick search — so typing a supplier name costs one request, not one per
   * keystroke. onSearch also resets to page 1; without that, a term that
   * narrows the result set strands the user on a page that no longer exists.
   */
  const handleSupplierSearch = (value: string) => {
    onFilterChange('supplier', value);
    if (supplierSearchTimerRef.current) clearTimeout(supplierSearchTimerRef.current);
    supplierSearchTimerRef.current = setTimeout(() => onSearch?.(), 500);
  };

  useEffect(() => () => {
    if (supplierSearchTimerRef.current) clearTimeout(supplierSearchTimerRef.current);
  }, []);

  const activeFilterCount = Object.entries(filters).filter(([key, val]) =>
    val && !['poNumber', 'supplier', 'customer'].includes(key)
  ).length + (textInputs.poNumber ? 1 : 0) + (textInputs.supplier ? 1 : 0) + (textInputs.customer ? 1 : 0);

  return (
    <div className="space-y-4">
      {/* Hidden file inputs for upload */}
      <input ref={pdfInputRef} type="file" accept=".pdf" className="hidden" onChange={handleFileSelected} />
      <input ref={certInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleFileSelected} />

      {/* CR-6446: Filter Header Bar - matches Deliveries tab design */}
      <Card>
        <CardHeader className="py-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            {/* CR-6446: Title and search grouped together on the left */}
            <div className="flex items-center gap-4">
              <CardTitle className="text-base text-xl">
                {isSupplier ? 'Filter SO Headers' : 'Filter PO Headers'}
              </CardTitle>
              {/* CR-7232: supplier search on the toolbar itself, so filtering by
                  supplier no longer means opening the filter panel. Writes the
                  same textInputs.supplier slot the panel's Supplier select and
                  the ?supplier= deep link already use — one value, so the grid,
                  the export and the badge cannot disagree.
                  Customer view only: on the supplier side every row is that
                  supplier's own, so the box would filter nothing. */}
              {!isSupplier && (
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-foreground pointer-events-none" />
                  <input
                    type="text"
                    value={textInputs.supplier}
                    onChange={(e) => handleSupplierSearch(e.target.value)}
                    placeholder="Search Supplier"
                    className="pl-8 pr-2 py-1.5 text-sm border-2 border-foreground rounded-lg w-64 bg-background"
                    aria-label="Search purchase orders by supplier name"
                  />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onRefresh}
                className="px-3 py-2 rounded-lg border border-border text-sm flex items-center gap-2 hover:bg-muted transition-colors"
                title="Refresh data"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
              <button
                onClick={handleExport}
                disabled={exporting || totalCount === 0}
                className="px-3 py-2 rounded-lg border border-border text-sm flex items-center gap-2 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Export to Excel"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {exporting ? 'Exporting...' : 'Export'}
              </button>
              <button
                onClick={onToggleFilters}
                className={`px-4 py-2 rounded-lg border text-sm font-medium flex items-center gap-2 transition-colors ${
                  showFilters
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border hover:bg-muted'
                }`}
              >
                <Filter className="h-4 w-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    showFilters
                      ? 'bg-primary-foreground/20 text-primary-foreground'
                      : 'bg-primary text-primary-foreground'
                  }`}>
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </CardHeader>

        {/* Filter Sections - only visible when filters are shown */}
        {showFilters && (
          isSupplier ? (
            // CR-6446: Supplier View - matching Deliveries tab layout
            <CardContent className="space-y-4 p-0">
              <POHeaderFilterSections
                filters={filters}
                textInputs={textInputs}
                siteOptions={viewScopedSites}
                supplierGroupOptions={supplierGroupOptions}
                supplierSiteOptions={allSuppliers.map((s) => ({ value: s.name, label: s.name }))}
                customerOptions={allCustomers.map((c) => ({ value: c.name, label: c.name }))}
                commodityGroupOptions={commodityGroupOptions}
                commodityTypeOptions={commodityTypeOptions}
                divisionOptions={divisionOptions}
                siteCountries={siteCountries}
                supplierCountries={supplierCountries}
                rawFilterOptions={rawFilterOptions}
                onFilterChange={onFilterChange}
                onApply={onSearch}
                isSupplier={isSupplier}
              />
              {/* CR-6446: Filter footer - Clear all left, Apply filters right (matching Deliveries tab) */}
              <div className="flex items-center justify-between px-6 pb-4">
                <button
                  onClick={onClearFilters}
                  className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-muted flex items-center gap-1.5 transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                  Clear all
                </button>
                <button
                  onClick={onSearch}
                  className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 flex items-center gap-1.5 transition-colors"
                >
                  <Search className="h-3.5 w-3.5" />
                  Apply filters
                </button>
              </div>
            </CardContent>
          ) : (
            // Customer View - matching Deliveries tab layout
            <CardContent className="space-y-4 p-0">
              <POHeaderFilterSections
                filters={filters}
                textInputs={textInputs}
                siteOptions={viewScopedSites}
                supplierGroupOptions={supplierGroupOptions}
                supplierSiteOptions={allSuppliers.map((s) => ({ value: s.name, label: s.name }))}
                customerOptions={allCustomers.map((c) => ({ value: c.name, label: c.name }))}
                commodityGroupOptions={commodityGroupOptions}
                commodityTypeOptions={commodityTypeOptions}
                divisionOptions={divisionOptions}
                siteCountries={siteCountries}
                supplierCountries={supplierCountries}
                rawFilterOptions={rawFilterOptions}
                onFilterChange={onFilterChange}
                onApply={onSearch}
                isSupplier={isSupplier}
              />
              {/* CR-6446: Filter footer - Clear all left, Apply filters right (matching Deliveries tab) */}
              <div className="flex items-center justify-between px-6 pb-4">
                <button
                  onClick={onClearFilters}
                  className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-muted flex items-center gap-1.5 transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                  Clear all
                </button>
                <button
                  onClick={onSearch}
                  className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 flex items-center gap-1.5 transition-colors"
                >
                  <Search className="h-3.5 w-3.5" />
                  Apply filters
                </button>
              </div>
            </CardContent>
          )
        )}
      </Card>

      {/* Orders Table - CR-6446: View Bar with row count and status tabs */}
      <Card>
        <Tabs value={activeTab} onValueChange={onTabChange}>
          <CardHeader>            
            {/* CR-6446: View Bar - shows row count on left, status tabs on right per design */}
            <div className="flex items-center justify-between">
              <div>
              <CardTitle>{isSupplier ? 'Sales Order Headers' : 'Purchase Order Headers'}</CardTitle>
              <div className="flex items-center gap-4 mt-1">
                <p className="text-sm text-muted-foreground">
                  Showing {orders.length} of {totalCount.toLocaleString()} orders
                </p>
                {openOrderValue > 0 && (
                  <span
                    className="text-sm font-medium text-primary"
                    title={openOrderValueUnscoped
                      ? 'Value of all open orders in the current view; the PO search and strategic filters are not applied to this figure.'
                      : 'Value of open orders matching the current view and filters.'}
                  >
                    Open Order Value{openOrderValueUnscoped ? ' (all open orders)' : ''}: {formatCurrencyFromBase(openOrderValue)}
                  </span>
                )}
              </div>
              </div>
              <TabsList>
                <TabsTrigger value="all">
                  All orders{tabCounts['all'] !== undefined ? ` (${tabCounts['all'].toLocaleString()})` : ''}
                </TabsTrigger>
                <TabsTrigger value="open">
                  Open{tabCounts['open'] !== undefined ? ` (${tabCounts['open'].toLocaleString()})` : ''}
                </TabsTrigger>
                <TabsTrigger value="closed">
                  Closed{tabCounts['closed'] !== undefined ? ` (${tabCounts['closed'].toLocaleString()})` : ''}
                </TabsTrigger>
              </TabsList>
            </div>
          </CardHeader>

          <TabsContent value={activeTab} className="m-0">
            <CardContent>
              {orders.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {textInputs.poNumber || textInputs.supplier || textInputs.customer || filters.status || filters.dateFrom || filters.dateTo || filters.pdfAttached || filters.ackStatus
                      ? 'No purchase orders match your filters'
                      : 'No purchase orders found'}
                  </p>
                  {(textInputs.poNumber || textInputs.supplier || textInputs.customer || filters.status || filters.dateFrom || filters.dateTo || filters.pdfAttached || filters.ackStatus) && (
                    <button
                      onClick={onClearFilters}
                      className="mt-4 text-sm text-primary hover:underline"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {/* Top Pagination */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    itemsPerPage={50}
                    onPageChange={onPageChange}
                    itemLabel="orders"
                  />

                  {/* CR-3866: Added max-height and sticky header support */}
                  <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-350px)]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 z-20">
                        <tr className="border-b bg-muted/80 backdrop-blur-sm">
                          <SortHeader field="po_number" label={isSupplier ? "SO Number" : "PO Number"} />
                          <SortHeader field="status" label="Status" />
                          <SortHeader field="customer__name" label="Customer" />
                          <th className="text-left py-3 px-3 font-medium">Supplier Group</th>
                          <SortHeader field="supplier__name" label="Supplier" />
                          <SortHeader field="total_value" label="Total Value" align="right" />
                          <SortHeader field="line_count" label="Lines" align="center" />
                          <SortHeader field="open_line_count" label="Open" align="center" />
                          <SortHeader field="closed_line_count" label="Closed" align="center" />
                          {/* CR-6679: Header-level acknowledgement status */}
                          <th className="text-center py-3 px-3 font-medium">Ack Status</th>
                          <th className="text-center py-3 px-3 font-medium">PDF</th>
                          <th className="text-center py-3 px-3 font-medium">Certs</th>
                          <SortHeader field="issue_date" label="Issue Date" />
                          <th className="text-left py-3 px-3 font-medium">Created By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) => (
                            <tr
                              key={order.id}
                              onClick={() => onRowClick(order.id)}
                              className={`border-b hover:bg-muted/50 transition-colors cursor-pointer ${order.status === 'closed' ? 'opacity-50 bg-muted/30' : ''}`}
                            >
                              {/* PO Number */}
                              <td className="py-3 px-3">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onPONumberClick(order.po_number);
                                    }}
                                    className="font-medium text-primary hover:underline"
                                  >
                                    {order.po_number}
                                  </button>
                                  {hasOnHoldLines(order) && (
                                    <div title="Has lines on hold">
                                      <AlertCircle className="h-4 w-4 text-red-500" />
                                    </div>
                                  )}
                                </div>
                              </td>
                              {/* Status */}
                              <td className="py-3 px-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  order.status === 'closed'
                                    ? 'bg-slate-100 text-slate-800'
                                    : 'bg-green-100 text-green-800'
                                }`}>
                                  {order.status === 'closed' ? 'Closed' : 'Open'}
                                </span>
                              </td>
                              {/* Customer */}
                              <td className="py-3 px-3">
                                {order.customer_name || order.customer_name || '-'}
                              </td>
                              {/* Supplier Group */}
                              <td className="py-3 px-3">
                                {order.supplier_group_name || '-'}
                              </td>
                              {/* Supplier */}
                              <td className="py-3 px-3">
                                <a
                                  href={`/suppliers/${order.supplier}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-primary hover:underline"
                                >
                                  {order.supplier_name}
                                </a>
                              </td>
                              {/* Total Value */}
                              <td className="py-3 px-3 text-right">
                                {formatCurrency(parseFloat(order.total_value?.toString() || '0'), order.currency, 2)}
                              </td>
                              {/* Line Counts */}
                              <td className="py-3 px-3 text-center text-sm">{order.line_count ?? '-'}</td>
                              <td className="py-3 px-3 text-center text-sm">
                                {order.open_line_count ? (
                                  <span className="text-orange-600 font-medium">{order.open_line_count}</span>
                                ) : '-'}
                              </td>
                              <td className="py-3 px-3 text-center text-sm">
                                {order.closed_line_count ? (
                                  <span className="text-green-600">{order.closed_line_count}</span>
                                ) : '-'}
                              </td>
                              {/* CR-6679: Header-level acknowledgement status */}
                              <td className="py-3 px-3 text-center">
                                {order.ack_status_summary === 'fully_acknowledged' && (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Fully Ack'd
                                  </span>
                                )}
                                {order.ack_status_summary === 'partially_acknowledged' && (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                    Partial
                                  </span>
                                )}
                                {order.ack_status_summary === 'not_acknowledged' && (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    Not Ack'd
                                  </span>
                                )}
                                {order.ack_status_summary === 'no_open_lines' && (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                    N/A
                                  </span>
                                )}
                                {!order.ack_status_summary && '-'}
                              </td>
                              {/* PDF - clickable for upload */}
                              <td className="py-3 px-3 text-center">
                                {hasPDFAttached(order) ? (
                                  <div className="flex justify-center" title="PDF attached">
                                    <FileText className="h-4 w-4 text-green-600" />
                                  </div>
                                ) : (
                                  <button
                                    onClick={(e) => handleUploadClick(e, order.id, 'po_pdf')}
                                    className="flex justify-center mx-auto hover:bg-muted rounded p-1 transition-colors"
                                    title="Upload PDF"
                                  >
                                    <Plus className="h-4 w-4 text-gray-400 hover:text-primary" />
                                  </button>
                                )}
                              </td>
                              {/* Material Certs - clickable for upload */}
                              <td className="py-3 px-3 text-center">
                                {hasMaterialCert(order) ? (
                                  <div className="flex justify-center" title="Material certificate attached">
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                  </div>
                                ) : (
                                  <button
                                    onClick={(e) => handleUploadClick(e, order.id, 'material_cert_customer')}
                                    className="flex justify-center mx-auto hover:bg-muted rounded p-1 transition-colors"
                                    title="Upload Certificate"
                                  >
                                    <Plus className="h-4 w-4 text-gray-400 hover:text-primary" />
                                  </button>
                                )}
                              </td>
                              {/* Issue Date */}
                              <td className="py-3 px-3">
                                {formatDate(order.issue_date)}
                              </td>
                              {/* Created By */}
                              <td className="py-3 px-3">
                                {order.created_by_name || '-'}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Bottom Pagination */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    itemsPerPage={50}
                    onPageChange={onPageChange}
                    itemLabel="orders"
                  />
                </>
              )}
            </CardContent>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
